export interface TenantDomain {
  name: string;
  verified: boolean;
}

export interface Tenant {
  id: string;
  name: string;
  alias: string;
  enabled: boolean;
  description?: string;
  domains: TenantDomain[] | null;
}

export interface TenantSsoProvider {
  alias: string;
  /** Keycloak provider type, e.g. "saml", "oidc". */
  providerId: string;
}

/**
 * Enriched single-organization payload — returned by GET /organizations/{id}
 * and the create response only; the list payload stays the plain `Tenant`.
 * `created` is null for organizations that predate the stamping (render "—").
 */
export interface TenantDetail extends Tenant {
  created: string | null; // ISO instant
  contact: string | null;
  region: string | null;
  ssoConfigured: boolean;
  ssoProviders: TenantSsoProvider[];
}

/** Request body for creating/updating a tenant. Domains only need `name` on write;
 *  `verified` is server-assigned and returned on read. Contact/region are set via
 *  `attributes` (e.g. { contact: ["ops@acme.com"], region: ["eu-west"] });
 *  `created` is server-stamped and never sent. */
export interface TenantRequest {
  name: string;
  alias: string;
  enabled: boolean;
  description?: string;
  domains: { name: string }[] | null;
  attributes?: Record<string, string[]>;
}

/** Write-side attributes map from contact/region values. Always include it in
 *  a PUT (even empty) — updates are full-replacement, and omitting a key is
 *  how an attribute gets cleared intentionally rather than wiped by accident. */
export function tenantAttributes(contact: string | null, region: string | null): Record<string, string[]> {
  const attributes: Record<string, string[]> = {};
  if (contact?.trim()) attributes['contact'] = [contact.trim()];
  if (region?.trim()) attributes['region'] = [region.trim()];
  return attributes;
}
